import React from 'react';
import { CardViewContentBlockProps } from '../types';
import { Box, Typography } from '@mui/material';
import CategoryCard from '../../../common/category-card/CategoryCard';
import CategoryCardGrid from '../../../common/category-card/CategoryCardGrid';
import { CATEGORY_CARD_ACCENTS } from '../../../common/category-card/types';
import TerminationIcon from '../../../../assets/svg/termination.svg?react';
import PolicyIcon from '../../../../assets/svg/policy.svg?react';
import ProposalIcon from '../../../../assets/svg/proposal.svg?react';
import ProjectMaterialsIcon from '../../../../assets/svg/project-materials.svg?react';
import InvoicingIcon from '../../../../assets/svg/invoicing.svg?react';
import ReceiptIcon from '../../../../assets/svg/receipt-icon.svg?react';
import { useTranslation } from 'react-i18next';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import DocumentListVault from '../../document-vault/components/document-list-vault/DocumentListVault';
import DocumentsBlock, { DocumentsActions } from '../../../common/documents-block/DocumentsBlock';
import styles from '../SupplierProfile.module.scss';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { RootState } from '../../../store';
import { ProgressOverlay } from '../../../common/progress-overlay/ProgressOverlay';
import { useDocumentsMetaData } from '../../document-vault/hooks/useDocumentsWorkflowsMetadata';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import OperationsIcon from '../../../../assets/svg/business-to-consumer-plan.svg?react';
import AccountingIcon from '../../../../assets/svg/view-list.svg?react';
import { clearDocuments } from '../../../slices/documents/documentsSlice';

export const SuppliersCategoryContent = ({
  documents,
  workflow,
  setWorkflow,
  showWorkflowBreadcrumbs,
  setShowWorkflowBreadcrumbs,
  cardView,
  documentsActions,
}: CardViewContentBlockProps) => {
  const { t } = useTranslation();

  const location = useLocation();
  const profileId = location.pathname.split('/supplier-profiles/')[1]?.split('/')[0];
  const dispatch = useAppDispatch();
  const documentUploadsLoading = useAppSelector((state: RootState) => state.documents.status);
  const { uploadedDocumentsMetaData } = useDocumentsMetaData('All', 'suppliers', Number(profileId));

  const getDocsFilteredByEmptyWorkflowCount = () => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === '');
    return metaData ? metaData.count : 0;
  };

  const getDocsCount = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData
      ? metaData.count +
          (workflow === t('labels.generalAdministration')
            ? getDocsFilteredByEmptyWorkflowCount()
            : 0)
      : 0;
  };

  const hasNewDocuments = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData ? metaData.isNew : false;
  };

  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.supplier.agreement'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.supplier.proposalOrQuote'):
        return <ProposalIcon />;
      case t('tagsLabels.documentWorkflow.supplier.projectMaterials'):
        return <ProjectMaterialsIcon />;
      case t('tagsLabels.documentWorkflow.supplier.invoicing'):
        return <InvoicingIcon />;
      case t('tagsLabels.documentWorkflow.supplier.receipts'):
        return <ReceiptIcon />;
      case t('tagsLabels.documentWorkflow.supplier.accounting'):
        return <AccountingIcon />;
      case t('tagsLabels.documentWorkflow.supplier.termination'):
        return <TerminationIcon />;
      case t('tagsLabels.documentWorkflow.supplier.generalAdministration'):
        return <OperationsIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.supplier.proposalOrQuote'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.supplier.agreement'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.supplier.invoicing'):
        return '3. ';
      case t('tagsLabels.documentWorkflow.supplier.receipts'):
        return '4. ';
      case t('tagsLabels.documentWorkflow.supplier.projectMaterials'):
        return '5. ';
      case t('tagsLabels.documentWorkflow.supplier.accounting'):
        return '6. ';
      case t('tagsLabels.documentWorkflow.supplier.termination'):
        return '7. ';
      case t('tagsLabels.documentWorkflow.supplier.generalAdministration'):
        return '8. ';
    }
  };

  return (
    <>
      {/* Workflows subheader + documents actions (workflow cards view) in one row. */}
      {(!workflow || (documentsActions && !(workflow && showWorkflowBreadcrumbs))) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          {!workflow && (
            <Typography
              variant='h3'
              component='h3'
              sx={{ margin: 0, fontSize: '15px', lineHeight: '24px' }}
            >
              {t('headers.workflows')}
            </Typography>
          )}
          {documentsActions && !(workflow && showWorkflowBreadcrumbs) && (
            <DocumentsActions {...documentsActions} standalone />
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
          paddingBottom: '24px',
        }}
      >
        {workflow && showWorkflowBreadcrumbs ? (
          <>
            <Box className={styles.breadcrumbs}>
              <Typography
                id='supplierProfilesSuppliersCategoryContentWorkflowsLink'
                variant='h3'
                component='h3'
                className={styles.documentViewText}
                onClick={() => {
                  setWorkflow(null);
                  setShowWorkflowBreadcrumbs(false);
                  dispatch(clearDocuments());
                }}
              >
                {t('headers.workflows')}
              </Typography>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {workflow === t('headers.general') ? t('labels.generalUnassigned') : workflow}
              </Typography>
            </Box>
            {/* Open workflow: the documents actions are attached to the table. */}
            <Box style={{ position: 'relative', width: '100%' }}>
              <DocumentsBlock actions={documentsActions}>
                <DocumentListVault
                  cardView={cardView}
                  compact
                  embedded={!!documentsActions}
                  documents={[...documents] /*getDocsFilteredByWorkflow(workflow)*/}
                />
                {documentUploadsLoading === 'loading' && <ProgressOverlay position='absolute' />}
              </DocumentsBlock>
            </Box>
          </>
        ) : (
          <CategoryCardGrid id='supplierProfilesSuppliersCategoryContentGrid'>
            {TAGS_LABELS.documentWorkflow.supplier.map((el) => (
              <CategoryCard
                id={`supplierProfilesSuppliersCategoryContentSupplierCard_${el}`}
                title={getWorkflowNumber(t(el)) + t(el)}
                countLabel={t(
                  getDocsCount(t(el)) === 1
                    ? 'categoryCards.documentCountSingular'
                    : 'categoryCards.documentCountPlural',
                  { count: getDocsCount(t(el)) }
                )}
                accent={CATEGORY_CARD_ACCENTS.Suppliers}
                onClick={() => {
                  setWorkflow(t(el));
                  setShowWorkflowBreadcrumbs(true);
                }}
                icon={getIcon(t(el))}
                key={el}
                hasNewLabel={
                  el === 'tagsLabels.documentWorkflow.supplier.generalAdministration'
                    ? hasNewDocuments(t(el)) || hasNewDocuments('')
                    : hasNewDocuments(t(el))
                }
              />
            ))}
          </CategoryCardGrid>
        )}
      </Box>
    </>
  );
};
